import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetWorkspacesByTenant } from '@/api/workspaces/useGetWorkspacesByTenant';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import Loader from '@/component-library/primitives/Loader/Loader';
import { isPendingClerkTenantLink } from '@/hooks/useLinkPendingClerkTenant';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import {
  selectCurrentWorkspace,
  setLoading,
  setSelectedWorkspace,
  setWorkspaces,
} from '@/modules/Dashboard/Workspaces/workspaceSlice';

type DashboardRoutesWrapperProps = {
  children: React.ReactNode;
};

const DashboardRoutesWrapper = ({ children }: DashboardRoutesWrapperProps) => {
  const { user, isLoading: authLoading } = useAuth();
  const { data = [], isLoading, isError, isFetching, isFetched, isIdle } = useGetWorkspacesByTenant();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const currentWorkspace = useAppSelector(selectCurrentWorkspace);
  const hasInitialized = useRef(false);

  // Use useMemo to prevent parsedWorkspace from changing on every render
  const parsedWorkspace = useMemo(() => {
    const storedWorkspace = sessionStorage.getItem('workspace');
    return storedWorkspace ? JSON.parse(storedWorkspace) : null;
  }, []); // Empty dependency array - only parse once on mount

  useEffect(() => {
    if (data.length && !hasInitialized.current) {
      dispatch(setWorkspaces(data));
      dispatch(setLoading(isLoading));

      const mergeWithFresh = (partial: { id: string }) => {
        const fresh = data.find((w) => w.id === partial.id);
        return fresh ? { ...partial, ...fresh } : partial;
      };

      // Prefer API fields (e.g. stripeFinancialFeaturesEnabled) over sessionStorage snapshot
      if (parsedWorkspace && data.some((workspace) => workspace.id === parsedWorkspace.id)) {
        dispatch(setSelectedWorkspace(mergeWithFresh(parsedWorkspace)));
      } else if (currentWorkspace && data.some((workspace) => workspace.id === currentWorkspace.id)) {
        dispatch(setSelectedWorkspace(mergeWithFresh(currentWorkspace)));
      } else {
        dispatch(setSelectedWorkspace(data[0]));
      }

      hasInitialized.current = true;
    }
  }, [data, dispatch, isLoading, parsedWorkspace, currentWorkspace]);

  // Save workspace to sessionStorage when it changes (but not during initial load)
  const currentWorkspaceFromStore = useAppSelector(selectCurrentWorkspace);
  useEffect(() => {
    if (currentWorkspaceFromStore && data.length > 0) {
      const storedWorkspace = sessionStorage.getItem('workspace');
      const currentWorkspaceString = JSON.stringify(currentWorkspaceFromStore);

      // Only save if it's different from what's stored to avoid infinite loops
      if (storedWorkspace !== currentWorkspaceString) {
        sessionStorage.setItem('workspace', currentWorkspaceString);
      }
    }
  }, [currentWorkspaceFromStore, data.length]);

  // Auth hydrates async; workspace query stays disabled (idle) until then. React Query reports
  // isFetched: false while idle — do not treat that as "still loading" or we spin forever and never fetch.
  if (authLoading) {
    return (
      <div className="flex min-h-[50vh] flex-1 items-center justify-center bg-custom-silver p-8">
        <Loader />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (isIdle && isPendingClerkTenantLink()) {
    return (
      <div className="flex min-h-[50vh] flex-1 items-center justify-center bg-custom-silver p-8">
        <Loader />
      </div>
    );
  }

  if (isIdle) {
    // Query disabled: no Bearer (REACT_APP_DEV_JWT unset in bundle) while user is signed in.
    return (
      <div className="flex min-h-[50vh] flex-1 flex-col items-center justify-center gap-2 bg-custom-silver p-8 text-center text-custom-darkSilver">
        <p className="max-w-md font-medium text-custom-black">
          Missing API bearer token for local dev. Set REACT_APP_DEV_JWT in web/env/.env.local.user and restart the dev
          server (pnpm start:web).
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-[50vh] flex-1 items-center justify-center bg-custom-silver p-8 text-center text-custom-darkSilver">
        {t('CommonErrors.fetchingWorkspacesFailure')}
      </div>
    );
  }

  if (isLoading || isFetching || !isFetched) {
    return (
      <div className="flex min-h-[50vh] flex-1 items-center justify-center bg-custom-silver p-8">
        <Loader />
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="flex min-h-[50vh] flex-1 items-center justify-center bg-custom-silver p-8 text-center text-custom-darkSilver">
        {t('CommonErrors.emptyWorkspacesResponseFailure')}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col p-4 pl-0">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col rounded-2xl bg-white">{children}</div>
    </div>
  );
};

export default DashboardRoutesWrapper;
